import type { ButtonHTMLAttributes, ComponentType, SVGProps } from 'react';
import { forwardRef } from 'react';
import type { Interpolation } from 'styled-components';

import type { NonTransientProp } from 'utils/styledUtils';

import { SpinnerDarkIcon, SpinnerLightIcon } from 'components/ui/icons';
import { ElementTestId } from 'enums/testing';

import type { StyledButtonProps } from './Button.styles';

import {
  ButtonContent,
  ButtonText,
  CountBadge,
  IconWrapper,
  LoadingOverlay,
  SpinnerContainer,
  StyledButton,
} from './Button.styles';

type ButtonIcon = ComponentType<SVGProps<SVGSVGElement>>;

type ButtonIconPosition = 'leading' | 'trailing';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement>, NonTransientProp<StyledButtonProps> {
  /**
   * Style override for the ButtonContent wrapper
   */
  contentStyleOverride?: Interpolation<object>;
  /**
   * Numeric counter displayed as a badge after button text
   */
  count?: number;
  /**
   * Icon component to display (pass component reference, not JSX element)
   */
  icon?: ButtonIcon;
  /**
   * Position of the icon relative to button text
   */
  iconPosition?: ButtonIconPosition;
}

const Button = forwardRef<HTMLButtonElement, Props>(
  (
    {
      active,
      children,
      contentStyleOverride,
      count,
      disabled,
      fullWidth,
      icon: Icon,
      iconPosition = 'leading',
      loading,
      size = 'medium',
      type = 'button',
      variant = 'secondary',
      ...props
    },
    ref
  ) => {
    const isLeading = iconPosition === 'leading';
    const isIconOnly = !!Icon && (children === undefined || children === null);
    const SpinnerIcon = variant === 'secondary' || variant === 'ghost' ? SpinnerDarkIcon : SpinnerLightIcon;

    return (
      <StyledButton
        $active={active}
        $fullWidth={fullWidth}
        $iconOnly={isIconOnly}
        $loading={loading}
        $size={size}
        $variant={variant}
        aria-busy={loading || undefined}
        aria-pressed={active}
        data-icon-only={isIconOnly || undefined}
        data-icon-position={Icon && !isIconOnly ? iconPosition : undefined}
        data-size={size}
        data-variant={variant}
        disabled={disabled}
        ref={ref}
        type={type}
        {...props}
      >
        <ButtonContent
          $loading={loading}
          $styleOverride={contentStyleOverride}
          data-testid={ElementTestId.BUTTON_CONTENT}
        >
          {isIconOnly ? (
            <IconWrapper aria-hidden="true" data-testid={ElementTestId.BUTTON_ICON}>
              <Icon />
            </IconWrapper>
          ) : (
            <>
              {Icon && isLeading ? (
                <IconWrapper aria-hidden="true" data-testid={ElementTestId.BUTTON_ICON}>
                  <Icon />
                </IconWrapper>
              ) : null}
              <ButtonText>{children}</ButtonText>
              {Icon && !isLeading ? (
                <IconWrapper aria-hidden="true" data-testid={ElementTestId.BUTTON_ICON}>
                  <Icon />
                </IconWrapper>
              ) : null}
              {count != null && <CountBadge>{count}</CountBadge>}
            </>
          )}
        </ButtonContent>
        {loading ? (
          <LoadingOverlay aria-hidden="true" data-testid={ElementTestId.BUTTON_SPINNER}>
            <SpinnerContainer>
              <SpinnerIcon />
            </SpinnerContainer>
          </LoadingOverlay>
        ) : null}
      </StyledButton>
    );
  }
);

Button.displayName = 'Button';

export default Button;
